import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Layers, 
  ShieldCheck, 
  Clock, 
  MapPin, 
  ArrowRight, 
  Phone, 
  Mail, 
  Sparkles, 
  Ruler, 
  Wrench, 
  HelpCircle, 
  Compass, 
  CheckCircle2, 
  AlertCircle,
  FileText,
  Trees,
  Check
} from 'lucide-react';
import Layout from '../components/Layout';
import ContactForm from '../components/ContactForm';
import FAQSection from '../components/FAQSection';
import { getOptimizedImageUrl } from '../lib/utils';

export default function Terrasseskruer() {
  const terraceImage = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/traeterrasse-skruefundament.jpg", { width: 1000, quality: 85 });
  const screwImage = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/jordskruer-teknisk-detalje.jpg", { width: 800, quality: 85 });
  const installationImage = getOptimizedImageUrl("https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/silkeborg-traeterrasse-skruefundament.jpg", { width: 800, quality: 85 });

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Terrasseskruer til terrassefundament - CM Skruefundament ApS",
    "description": "Terrasseskruer og jordskruer til stærke terrassefundamenter uden omfattende gravearbejde og betonhærdning i hele Danmark.",
    "brand": {
      "@type": "Brand",
      "name": "CM Skruefundament"
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "DKK",
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "CM Skruefundament ApS",
        "url": "https://cmskruefundament.dk"
      }
    }
  };

  const benefits = [
    {
      title: 'Mindre gravearbejde',
      desc: 'Mindre gravearbejde og begrænset håndtering af overskudsjord på grunden.',
      icon: Trees
    },
    {
      title: 'Ingen hærdetid',
      desc: 'Ingen ventetid på hærdning af beton – slip for at vente i dagevis.',
      icon: Clock
    },
    {
      title: 'Præcise punkter',
      desc: 'Præcise fundamentpunkter tilpasset terrassekonstruktionen og bærelaget.',
      icon: Ruler
    },
    {
      title: 'Byg videre straks',
      desc: 'Mulighed for at montere bjælkelag og bygge videre umiddelbart efter monteringen.',
      icon: Sparkles
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Vurdering',
      desc: 'af terrasse, jordbund og adgangsforhold',
      icon: Compass
    },
    {
      number: '02',
      title: 'Dimensionering',
      desc: 'af skruer og fundamentpunkter',
      icon: Ruler
    },
    {
      number: '03',
      title: 'Opmåling & nedskruning',
      desc: 'med egnet specialudstyr',
      icon: Wrench
    },
    {
      number: '04',
      title: 'Bæreevnekontrol',
      desc: 'af bæreevnen før videre byggeri',
      icon: ShieldCheck
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>Terrasseskruer til et stærkt fundament under terrassen | Kontakt</title>
        <meta 
          name="description" 
          content="Terrasseskruer er kraftige stålskruer til bærende fundamenter under terrassen. Få rådgivning, dimensionering og professionel montering." 
        />
        <link rel="canonical" href="https://cmskruefundament.dk/terrasseskruer" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cmskruefundament.dk/terrasseskruer" />
        <meta property="og:title" content="Terrasseskruer til et stærkt fundament under terrassen | Kontakt" />
        <meta 
          property="og:description" 
          content="Terrasseskruer er kraftige stålskruer til bærende fundamenter under terrassen. Få rådgivning, dimensionering og professionel montering." 
        />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/1579e6f7-9ff1-4e95-aba1-981ac1255ac4.jpg" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://cmskruefundament.dk/terrasseskruer" />
        <meta name="twitter:title" content="Terrasseskruer til et stærkt fundament under terrassen | Kontakt" />
        <meta 
          name="twitter:description" 
          content="Terrasseskruer er kraftige stålskruer til bærende fundamenter under terrassen. Få rådgivning, dimensionering og professionel montering." 
        />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/06c9940a-d5a6-4016-a4ca-e876b6f16b3e/1579e6f7-9ff1-4e95-aba1-981ac1255ac4.jpg" />

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:24px_24px]"></div>
        
        {/* Background glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-orange-500/10 blur-[120px] rounded-full pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-orange-500/10 text-orange-400 text-sm font-semibold mb-6 border border-orange-500/20">
                <Layers className="w-4 h-4" />
                <span>Bærende Fundamenter til Træterrasser</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-6 leading-[1.15]">
                Terrasseskruer til et stærkt fundament under terrassen
              </h1>
              
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                Hos <Link to="/" className="font-bold text-white underline decoration-orange-400 hover:text-orange-300 transition-colors">CM Skruefundament</Link> bruger vi terrasseskruer som betegnelse for de kraftige stålskruer, der etableres i jorden og bærer terrassekonstruktionen. Der er altså ikke tale om de mindre skruer til terrassebrædder. Du kan købe relevante produkter eller få os til at rådgive, dimensionere og montere en samlet løsning.
              </p>

              {/* Distinguish Note */}
              <div className="bg-slate-800/80 border border-slate-700/80 rounded-xl p-4 mb-8 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-400 shrink-0 mt-0.5" />
                <p className="text-sm text-slate-300">
                  <strong className="text-white">Bemærk forskellen:</strong> Terrasseskruer her er selve de bærende jordskruer i varmgalvaniseret stål, som bærer remme, strøer og terrassekonstruktion i de faste jordlag.
                </p>
              </div>

              <div className="flex flex-wrap gap-4">
                <a
                  href="#kontakt"
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-orange-500/25 transition-all flex items-center gap-2 transform active:scale-95"
                >
                  Få et uforpligtende tilbud
                  <ArrowRight className="w-5 h-5" />
                </a>
                <Link
                  to="/skruer"
                  className="bg-slate-800/80 hover:bg-slate-700 text-white font-medium px-6 py-4 rounded-xl border border-slate-700 transition-all flex items-center gap-2"
                >
                  Se alle skruer & specifikationer
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5" id="kontakt-form">
              <ContactForm
                variant="hero"
                defaultService="Terrasseskruer"
                title="Få tilbud på terrasseskruer"
                subtitle="Beskriv din nye terrasse eller vedhæft mål/skitse, så beregner vi det optimale antal skruer og pris."
              />
            </div>
          </div>

          {/* Quick Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16 pt-12 border-t border-slate-800/80">
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 sm:p-5">
              <p className="text-2xl sm:text-3xl font-extrabold text-orange-400 mb-1">0 timer</p>
              <p className="text-sm text-slate-300">Ventetid på betonhærdning</p>
            </div>
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 sm:p-5">
              <p className="text-2xl sm:text-3xl font-extrabold text-orange-400 mb-1">Minimal</p>
              <p className="text-sm text-slate-300">Gravearbejde & havepåvirkning</p>
            </div>
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 sm:p-5">
              <p className="text-2xl sm:text-3xl font-extrabold text-orange-400 mb-1">100%</p>
              <p className="text-sm text-slate-300">Præcis afsætning med laser</p>
            </div>
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 sm:p-5">
              <p className="text-2xl sm:text-3xl font-extrabold text-orange-400 mb-1">Hele DK</p>
              <p className="text-sm text-slate-300">Jylland, Fyn og Sjælland</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: Hvorfor vælge jordskruer til terrasse? */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-orange-50 text-orange-600 text-sm font-semibold">
                <Sparkles className="w-4 h-4" />
                <span>Effektivt Alternativ til Beton</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
                Hvorfor vælge jordskruer til terrasse?
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed">
                <Link to="/jordskruer" className="font-bold text-slate-900 underline decoration-orange-500 hover:text-orange-600 transition-colors">Jordskruer</Link> til terrasse er et effektivt alternativ til støbte punktfundamenter. Skruerne monteres direkte i jorden uden omfattende udgravning og uden hærdetid. Det betyder, at du hurtigere kan komme videre med bjælkelag og terrasse, mens påvirkningen af have, belægning og omgivelser ofte bliver mindre.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Løsningen er relevant til både mindre terrasser ved huset og større eller hævede terrassekonstruktioner. Den kan også være værd at overveje på ujævnt terræn eller steder, hvor du vil skåne haven. Den endelige løsning afhænger altid af jordbund, højde, last og konstruktionens udformning.
              </p>

              <div className="pt-2">
                <ul className="space-y-3.5">
                  {[
                    'Mindre gravearbejde og begrænset håndtering af overskudsjord',
                    'Ingen ventetid på hærdning af beton',
                    'Præcise fundamentpunkter til terrassekonstruktionen',
                    'Mulighed for at bygge videre umiddelbart efter monteringen'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-slate-700 font-medium">
                      <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5">
                        <CheckCircle2 className="w-4 h-4" />
                      </div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits.map((benefit, idx) => {
                  const Icon = benefit.icon;
                  return (
                    <div 
                      key={idx}
                      className="bg-slate-50 border border-slate-200/80 rounded-2xl p-6 hover:shadow-lg hover:border-orange-200 transition-all"
                    >
                      <div className="w-12 h-12 rounded-xl bg-orange-500/10 text-orange-600 flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">{benefit.title}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed">{benefit.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Skruefundament terrasse, sådan finder vi den rette løsning */}
      <section className="py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-orange-100 text-orange-700 text-sm font-semibold mb-4">
              <Wrench className="w-4 h-4" />
              <span>Fra Planlægning til Montering</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Skruefundament terrasse, sådan finder vi den rette løsning
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Et godt fundament begynder med de rigtige oplysninger. Når vi planlægger et skruefundament til terrasse, ser vi på terrassens mål, forventede belastning, bjælkelag, højder og adgangsforhold. Jordbunden er afgørende, fordi skruerne skal nå ned i jordlag, der kan optage belastningen.
            </p>
          </div>

          {/* Subheading + Steps */}
          <div className="bg-white rounded-2xl border border-slate-200 p-8 sm:p-12 shadow-sm mb-12">
            <div className="max-w-3xl mb-10">
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                Fra opmåling til kontrolleret bæreevne
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Fundamentpunkterne afsættes præcist efter projektet, hvorefter terrasseskruerne skrues ned med egnet udstyr. Under monteringen følger vi modstanden og kontrollerer bæreevnen. Det giver et fagligt grundlag for at vurdere, om hver skrue står korrekt og kan optage den planlagte belastning.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {processSteps.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <div 
                    key={idx} 
                    className="relative bg-slate-50 border border-slate-200/80 rounded-xl p-6 flex flex-col justify-between"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-black text-orange-500/40">{step.number}</span>
                      <div className="w-10 h-10 rounded-lg bg-orange-500/10 text-orange-600 flex items-center justify-center">
                        <Icon className="w-5 h-5" />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-slate-900 mb-1">{step.title}</h4>
                      <p className="text-sm text-slate-600">{step.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Ready to build paragraph callout */}
            <div className="mt-10 p-6 bg-orange-50/70 border border-orange-200 rounded-xl flex items-start gap-4">
              <Clock className="w-6 h-6 text-orange-600 shrink-0 mt-0.5" />
              <p className="text-slate-700 leading-relaxed text-sm sm:text-base">
                Når fundamentet er færdigt, kan remme og bjælker normalt monteres med det samme. Der er ingen beton, som først skal hærde. Det gør planlægningen mere enkel og kan forkorte tiden frem til den næste del af byggeriet, samtidig med at arbejdet omkring terrassen holdes mere overskueligt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Hvor lange skal terrasseskruer være? + Case Silkeborg */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-orange-50 text-orange-600 text-sm font-semibold">
                <Ruler className="w-4 h-4" />
                <span>Dimensionering & Længder</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
                Hvor lange skal terrasseskruer være?
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed">
                Hvor lange skal terrasseskruer være? Der findes ikke én længde, som passer til alle terrasser. Længden skal vælges ud fra jordlagene og den belastning, terrassen overfører. Diameter, godstykkelse, antal fundamentpunkter og eventuelle forlængelser har også betydning for den samlede bæreevne.
              </p>

              {/* Case Highlight */}
              <div className="bg-slate-900 text-white rounded-2xl p-6 sm:p-8 border border-slate-800 shadow-xl relative overflow-hidden">
                <div className="absolute -right-8 -bottom-8 w-40 h-40 bg-orange-500/10 rounded-full blur-2xl pointer-events-none"></div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 bg-orange-500 text-white text-xs font-bold uppercase tracking-wider rounded-md">
                    Projektcase
                  </span>
                  <span className="text-slate-400 text-sm flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 text-orange-400" />
                    Silkeborg
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  Terrasse i Silkeborg – 15 skruer Ø76 × 2000 × 3 mm
                </h3>
                <p className="text-slate-300 leading-relaxed text-sm sm:text-base">
                  Ved en terrasse i Silkeborg monterede vi 15 skruer på Ø76 × 2000 × 3 mm i et område med risiko for højvande. Det var en projekttilpasset løsning og ikke en standard for andre terrasser. Se referencen under <Link to="/projekter" className="font-bold text-orange-400 underline hover:text-orange-300 transition-colors">vores projekter</Link>. Projektet viser netop, hvorfor mål, konstruktion og jordbund skal vurderes før valg af skruer.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-6">
                <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-orange-500" />
                  Hvad påvirker dimensioneringen?
                </h3>
                <div className="space-y-4">
                  <div className="p-4 bg-white rounded-xl border border-slate-200">
                    <p className="font-bold text-slate-900 text-sm">Bærende jordlag</p>
                    <p className="text-slate-600 text-xs mt-1">Dybden til det faste bærelag under muld og opfyld.</p>
                  </div>
                  <div className="p-4 bg-white rounded-xl border border-slate-200">
                    <p className="font-bold text-slate-900 text-sm">Belastning & Terrassens vægt</p>
                    <p className="text-slate-600 text-xs mt-1">Træsort, overdækning, spabad eller ekstra store spændvidder.</p>
                  </div>
                  <div className="p-4 bg-white rounded-xl border border-slate-200">
                    <p className="font-bold text-slate-900 text-sm">Højde over terræn</p>
                    <p className="text-slate-600 text-xs mt-1">Hævede terrasser stiller særlige krav til vridningsstabilitet.</p>
                  </div>
                  <div className="p-4 bg-white rounded-xl border border-slate-200">
                    <p className="font-bold text-slate-900 text-sm">Lokale forhold</p>
                    <p className="text-slate-600 text-xs mt-1">Vandstand, skråninger, trærødder eller svært tilgængeligt terræn.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Er et jordanker til terrasse det samme som en jordskrue? */}
      <section className="py-20 bg-slate-900 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/20 text-orange-400 text-sm font-semibold mb-4 border border-orange-500/30">
              <HelpCircle className="w-4 h-4" />
              <span>Begrebsafklaring</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Er et jordanker til terrasse det samme som en jordskrue?
            </h2>
            <p className="text-slate-300 text-lg leading-relaxed">
              Begreberne bruges ofte om hinanden, men funktionen bør styre valget. Et jordanker til terrasse bruges typisk til forankring, mens jordskruer indgår som bærende fundamentpunkter. Hvis terrassen skal bæres af løsningen, dimensionerer vi derfor et egentligt skruefundament efter jordbund og last.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Jordanker Card */}
            <div className="bg-slate-800/80 border border-slate-700 rounded-2xl p-6 sm:p-8 flex flex-col justify-between">
              <div>
                <div className="inline-block px-3 py-1 bg-slate-700 text-slate-300 text-xs font-semibold rounded-md uppercase tracking-wider mb-4">
                  Forankring
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Jordanker til terrasse</h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">
                  Bruges primært til at fastholde eller stormsikre lette konstruktioner og udstyr mod træk, vip og vindkræfter.
                </p>
              </div>
              <Link 
                to="/jordanker" 
                className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 text-sm font-semibold group pt-4 border-t border-slate-700/60"
              >
                Læs mere om jordankre
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Jordskrue Card */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-850 border-2 border-orange-500/40 rounded-2xl p-6 sm:p-8 flex flex-col justify-between relative shadow-xl">
              <div className="absolute top-4 right-4">
                <span className="px-2.5 py-0.5 bg-orange-500 text-white text-xs font-bold rounded-full">
                  Bærende
                </span>
              </div>
              <div>
                <div className="inline-block px-3 py-1 bg-orange-500/20 text-orange-400 text-xs font-semibold rounded-md uppercase tracking-wider mb-4 border border-orange-500/30">
                  Bærende Fundament
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Jordskruer / Skruefundament</h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">
                  Dimensionerede stålskruer, der overfører hele terrassens vægt og belastning ned til de faste bærende jordlag uden beton.
                </p>
              </div>
              <Link 
                to="/skruefundering" 
                className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 text-sm font-semibold group pt-4 border-t border-slate-700/60"
              >
                Læs mere om skruefundering
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Få hjælp til terrasseskruer i hele Danmark */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-orange-50 text-orange-600 text-sm font-semibold">
                <MapPin className="w-4 h-4" />
                <span>Landsdækkende Service</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
                Få hjælp til terrasseskruer i hele Danmark
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed">
                Hos CM Skruefundament kan du få hjælp til produktvalg eller vælge en samlet løsning med rådgivning, dimensionering og professionel montering. Vi tilbyder også systemer som <Link to="/groundplug" className="font-bold text-slate-900 underline decoration-orange-500 hover:text-orange-600 transition-colors">GroundPlug</Link>. Læs mere <Link to="/om-os" className="font-bold text-slate-900 underline decoration-orange-500 hover:text-orange-600 transition-colors">om os</Link>. Vi arbejder med terrassefundamenter i Jylland, på Fyn og på Sjælland og tilpasser altid anbefalingen til det konkrete projekt.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Send gerne terrassens mål, oplysninger om placering og højde samt billeder eller en tegning, hvis du har det. Så kan vi vurdere, hvilke oplysninger der mangler, og om du har brug for skruer som produkt eller en komplet løsning med montering. <Link to="/kontakt" className="font-bold text-orange-600 underline hover:text-orange-700 transition-colors">Kontakt os</Link> for en uforpligtende vurdering.
              </p>

              <div className="pt-4 flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:+4521406560"
                  className="inline-flex items-center gap-3 bg-slate-900 hover:bg-slate-800 text-white px-6 py-3.5 rounded-xl font-bold transition-all"
                >
                  <Phone className="w-5 h-5 text-orange-400" />
                  <span>+45 21 40 65 60</span>
                </a>
                <a
                  href="mailto:kontakt@cmskruefundament.dk"
                  className="inline-flex items-center gap-3 bg-slate-100 hover:bg-slate-200 text-slate-900 px-6 py-3.5 rounded-xl font-bold transition-all border border-slate-200"
                >
                  <Mail className="w-5 h-5 text-orange-500" />
                  <span>kontakt@cmskruefundament.dk</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-5">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-orange-500" />
                  Hvad kan du sende med for hurtigt tilbud?
                </h3>
                <ul className="space-y-3 text-sm text-slate-600">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                    <span>Terrassens mål (længde, bredde og forventet areal i m²)</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                    <span>Højde over eksisterende terræn (og eventuelle niveauspring)</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                    <span>Billeder af haven/grunden eller en simpel skitse</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                    <span>Om du ønsker professionel montage eller udelukkende køb af skruer</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection
        badgeText="Ofte stillede spørgsmål"
        title="Spørgsmål & svar om terrasseskruer"
        description="Få overblik over fordelene ved skruefundamenter til træterrasser, herunder niveauregulering, afstande, topbeslag og montage."
        themeColor="orange"
        bgClass="bg-white"
        items={[
          {
            question: "Hvorfor er skruefundamenter bedre til en træterrasse end støbte betonstolper?",
            answer: `Skruefundamenter til træterrasser sparer dig for det slidsomme gravearbejde, opblanding af mange sække tung beton samt ventetid på, at betonen hærder op. Skruerne monteres hurtigt og præcist i niveau, så strøer og bærende bjælker kan monteres direkte på de justerbare topbeslag med det samme.

Samtidig hæves trækonstruktionen fri af jordfugten, hvilket modvirker råd i træet og forlænger terrassens levetid betydeligt, ligesom fundamentet ikke påvirkes af vinterens frostskud.`
          },
          {
            question: "Hvordan håndterer man niveauforskelle og skrånende terræn med terrasseskruer?",
            answer: `Skruefundamenter er den absolut mest elegante og fleksible løsning på kuperede grunde og skrånende havearealer, hvor traditionel støbning kræver høje forskallinger eller terrænregulering. Ved at anvende jordskruer i varierende længder eller kombinere dem med forlængerrør og højdejusterbare beslag kan man ubesværet etablere et 100% vandret fundament, uanset hvor meget grunden falder.

Dette gør det nemt at bygge hævede terrasser i flere niveauer eller svævende dæk på udfordrende skråninger.`
          },
          {
            question: "Hvor tæt skal terrasseskruerne placeres, og hvordan beregnes afstanden?",
            answer: `Afstanden mellem skruefundamenterne afhænger af dimensionerne på dit bærende bjælkelag (strøer og bærelægter) samt terrassens forventede personbelastning og egenvægt. Som tommelfingerregel placeres skruerne typisk med en indbyrdes afstand på mellem 1,5 og 2,5 meter under de primære bærebjælker ved almindelige 45x145 mm eller 45x195 mm strøer.

Vi rådgiver dig gerne om den præcise placering og dimensionering ud fra dine terrassetegninger, så konstruktionen bliver fuldstændig stiv og fri for fjedring.`
          },
          {
            question: "Kan man selv montere terrasseskruer, eller bør man få professionel hjælp?",
            answer: `Til mindre og mellemstørrelse terrasser på almindelig havejord kan du sagtens selv foretage monteringen med vores brugervenlige manuelle installationsværktøj eller en god elektrisk maskine. Skal du derimod anlægge en stor tagterrasse, en højt hævet konstruktion eller bygge i hård og stenet jord, kan det være en stor fordel at lade vores erfarne montører klare opgaven med specialmaskiner.

Med vores professionelle montage sikrer du millimeterpræcis højdeafsætning med laser og et færdigt fundament på få timer.`
          },
          {
            question: "Hvilke typer topbeslag og overgange findes der til montering af træbjælker?",
            answer: `Vores terrasseskruer leveres med en bred vifte af specialudviklede topbeslag, der passer perfekt til enhver gængs trækonstruktion. Vi tilbyder U-beslag i forskellige bredder (f.eks. 45 mm, 70 mm, 90 mm) til direkte fastgørelse af bærebjælker, flade flanger med bolthuller samt højdejusterbare spindelbeslag, der giver mulighed for finjustering af millimeterhøjden efter montagen.

Alle beslag er udført i kraftigt varmgalvaniseret stål, som sikrer en solid og rustfri forbindelse mellem stålfundament og træværk.`
          }
        ]}
      />

      {/* Form Section */}
      <section id="kontakt" className="py-20 bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-orange-100 text-orange-700 text-sm font-semibold mb-3">
              <Mail className="w-4 h-4" />
              <span>Uforpligtende Tilbud</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              Få et uforpligtende tilbud på terrasseskruer
            </h2>
            <p className="text-slate-600 mt-2 max-w-xl mx-auto text-lg">
              Send os målene og detaljerne om din planlagte terrasse, så vender vi hurtigt tilbage med en faglig vurdering.
            </p>
          </div>
          <ContactForm defaultService="Terrasseskruer" />
        </div>
      </section>
    </Layout>
  );
}
